/**
=========================================================
* Soft UI Dashboard PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Ensure the theme overrides are applied across libs and apps
import './overrides';

import { createTheme } from '@mui/material/styles';
import colors from './legacy/colors';
import breakpoints from './design-system/breakpoints';
import typography, {
    DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS,
} from './legacy/typography';
import boxShadows from './legacy/boxShadows';
import borders from './legacy/borders';
import globals from './design-system/globals';
import boxShadow from './functions/boxShadow';
import hexToRgb from './functions/hexToRgb';
import linearGradient from './functions/linearGradient';
import pxToRem from './functions/pxToRem';
import rgba from './functions/rgba';

import sidenav from './legacy/components/sidenav';
import list from './legacy/components/list';
import listItem from './legacy/components/list/listItem';
import listItemText from './legacy/components/list/listItemText';
import card from './legacy/components/card';
import cardMedia from './legacy/components/card/cardMedia';
import cardContent from './legacy/components/card/cardContent';
import button from './legacy/components/button';
import iconButton from './legacy/components/iconButton';
import inputBase from './legacy/components/form/inputBase';
import menu from './legacy/components/menu';
import menuItem from './legacy/components/menu/menuItem';
import switchButton from './legacy/components/form/switchButton';
import divider from './legacy/components/divider';
import tableContainer from './legacy/components/table/tableContainer';
import tableHead from './legacy/components/table/tableHead';
import tableCell from './legacy/components/table/tableCell';
import linearProgress from './legacy/components/linearProgress';
import breadcrumbs from './legacy/components/breadcrumbs';
import avatar from './legacy/components/avatar';
import tooltip from './legacy/components/tooltip';
import appBar from './legacy/components/appBar';
import tabs from './legacy/components/tabs';
import tab from './legacy/components/tabs/tab';
import stepper from './legacy/components/stepper';
import step from './legacy/components/stepper/step';
import stepConnector from './legacy/components/stepper/stepConnector';
import stepLabel from './legacy/components/stepper/stepLabel';
import stepIcon from './legacy/components/stepper/stepIcon';
import select from './legacy/components/form/select';
import formControlLabel from './legacy/components/form/formControlLabel';
import formLabel from './legacy/components/form/formLabel';
import checkbox from './legacy/components/form/checkbox';
import radio from './legacy/components/form/radio';
import autocomplete from './legacy/components/form/autocomplete';
import input from './legacy/components/form/input';
import apexcharts from './legacy/components/apexcharts';
import flatpickr from './legacy/components/flatpickr';
import swal from './legacy/components/swal';
import container from './legacy/components/container';
import popover from './legacy/components/popover';
import buttonBase from './legacy/components/buttonBase';
import icon from './legacy/components/icon';
import svgIcon from './legacy/components/svgIcon';
import link from './legacy/components/link';
import dialog from './legacy/components/dialog';
import dialogTitle from './legacy/components/dialog/dialogTitle';
import dialogContent from './legacy/components/dialog/dialogContent';
import dialogContentText from './legacy/components/dialog/dialogContentText';
import dialogActions from './legacy/components/dialog/dialogActions';
import notistack from './legacy/components/notistack';
import lightColorMode from './design-system/color-modes/light';
import { borderRadius } from './design-system/borders';
import darkColorMode from './design-system/color-modes/dark';

const createUphealTheme = (colorMode /** @type {DesignSystemColorMode} */) => {
    return createTheme({
        breakpoints: { ...breakpoints },
        palette: { ...colors, ds: colorMode },
        typography: { ...typography },
        boxShadows: { ...boxShadows },
        borders: { ...borders, ds: { borderRadius: borderRadius } },
        functions: {
            boxShadow,
            hexToRgb,
            linearGradient,
            pxToRem,
            rgba,
        },
        components: {
            MuiTypography: {
                defaultProps: {
                    variantMapping: {
                        [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.h1]: 'h1',
                        [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.h2]: 'h2',
                        [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.h3]: 'h3',
                        [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.h4]: 'h4',
                        [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.h5]: 'h5',
                        [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.h6]: 'h6',
                        [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.xl]: 'span',
                        [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.lg]: 'span',
                        [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.md]: 'span',
                        [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.sm]: 'span',
                        [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.xs]: 'span',
                        [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.xxs]: 'span',
                    },
                },
            },
            MuiCssBaseline: {
                styleOverrides: {
                    ...globals,
                    ...apexcharts,
                    ...flatpickr,
                    ...swal,
                    ...container,
                    ...notistack,
                },
            },
            MuiDrawer: { ...sidenav },
            MuiList: { ...list },
            MuiListItem: { ...listItem },
            MuiListItemText: { ...listItemText },
            MuiCard: { ...card },
            MuiCardMedia: { ...cardMedia },
            MuiCardContent: { ...cardContent },
            MuiButton: { ...button },
            MuiIconButton: { ...iconButton },
            MuiInputBase: { ...inputBase },
            MuiMenu: { ...menu },
            MuiMenuItem: { ...menuItem },
            MuiSwitch: { ...switchButton },
            MuiDivider: { ...divider },
            MuiTableContainer: { ...tableContainer },
            MuiTableHead: { ...tableHead },
            MuiTableCell: { ...tableCell },
            MuiLinearProgress: { ...linearProgress },
            MuiBreadcrumbs: { ...breadcrumbs },
            MuiAvatar: { ...avatar },
            MuiTooltip: { ...tooltip },
            MuiAppBar: { ...appBar },
            MuiTabs: { ...tabs },
            MuiTab: { ...tab },
            MuiStepper: { ...stepper },
            MuiStep: { ...step },
            MuiStepConnector: { ...stepConnector },
            MuiStepLabel: { ...stepLabel },
            MuiStepIcon: { ...stepIcon },
            MuiSelect: { ...select },
            MuiFormControlLabel: { ...formControlLabel },
            MuiFormLabel: { ...formLabel },
            MuiCheckbox: { ...checkbox },
            MuiRadio: { ...radio },
            MuiAutocomplete: { ...autocomplete },
            MuiInput: { ...input },
            MuiOutlinedInput: { ...input },
            MuiFilledInput: { ...input },
            MuiPopover: { ...popover },
            MuiButtonBase: { ...buttonBase },
            MuiIcon: { ...icon },
            MuiSvgIcon: { ...svgIcon },
            MuiLink: { ...link },
            MuiDialog: { ...dialog },
            MuiDialogTitle: { ...dialogTitle },
            MuiDialogContent: { ...dialogContent },
            MuiDialogContentText: { ...dialogContentText },
            MuiDialogActions: { ...dialogActions },
        },
    });
};

export const theme = createUphealTheme(lightColorMode);
export const darkTheme = createUphealTheme(darkColorMode);

export default theme;
